/**
 * Utility functions for scroll behavior in modals
 */

import { useEffect } from 'react';

/**
 * Scrolls the modal content to the top with smooth animation
 * @param selector - CSS selector for the scrollable container (defaults to '.modal-content')
 * @param behavior - Scroll behavior ('smooth' | 'instant' | 'auto')
 * @param delay - Optional delay in milliseconds to wait before scrolling
 */
export const scrollToTop = (
  selector: string = '.modal-content',
  behavior: ScrollBehavior = 'smooth',
  delay: number = 0
): void => {
  const performScroll = () => {
    const scrollableElement = document.querySelector(selector);
    if (scrollableElement) {
      scrollableElement.scrollTo({ top: 0, behavior });
    }
  };

  if (delay > 0) {
    setTimeout(performScroll, delay);
  } else {
    performScroll();
  }
};

/**
 * Creates a scroll-to-top handler function for use in event handlers
 * @param selector - CSS selector for the scrollable container
 * @param behavior - Scroll behavior
 * @param delay - Optional delay in milliseconds
 * @returns Function that can be called in onClick handlers
 */
export const createScrollToTopHandler = (
  selector: string = '.modal-content',
  behavior: ScrollBehavior = 'smooth',
  delay: number = 0
) => {
  return () => scrollToTop(selector, behavior, delay);
};

/**
 * Hook for React components that provides scroll-to-top functionality
 * @param selector - CSS selector for the scrollable container
 * @param delay - Optional delay in milliseconds (defaults to 100ms for React state updates)
 * @returns Object with scrollToTop function and handler
 */
export const useScrollToTop = (selector: string = '.modal-content', delay: number = 100) => {
  const scrollToTopFn = () => scrollToTop(selector, 'smooth', delay);
  const scrollToTopHandler = createScrollToTopHandler(selector, 'smooth', delay);
  
  return {
    scrollToTop: scrollToTopFn,
    scrollToTopHandler,
  };
};

/**
 * Hook that provides scroll-to-top functionality with automatic scrolling on dependency changes
 * @param dependencies - Array of dependencies to watch for changes
 * @param selector - CSS selector for the scrollable container
 * @param delay - Optional delay in milliseconds
 * @returns Object with scrollToTop function and handler
 */
export const useScrollToTopOnChange = (
  dependencies: any[],
  selector: string = '.modal-content',
  delay: number = 100
) => {
  const scrollToTopFn = () => scrollToTop(selector, 'smooth', delay);
  const scrollToTopHandler = createScrollToTopHandler(selector, 'smooth', delay);
  
  // Auto-scroll when dependencies change
  useEffect(() => {
    scrollToTopFn();
  }, dependencies);
  
  return {
    scrollToTop: scrollToTopFn,
    scrollToTopHandler,
  };
};
